import React, { useCallback, useRef, useState } from 'react';

/**
 * Shared toast/notification — one app-wide, themed with the global tokens
 * (src/styles/theme.css). Mirrors the useConfirm() ergonomics. All spacing,
 * type, radius and colour come from the global CSS custom-property tokens
 * (space, fs, fw, radius, color) — no hardcoded values.
 *
 * Usage:
 *   const { notify, toastNode } = useToast();
 *   ...
 *   notify('Saved successfully', 'success');
 *   notify(extractError(e) || 'Save failed', 'error');
 *   ...
 *   return (<div> ... {toastNode} </div>);
 *
 * notify(message, kind?, opts?) → id
 *   kind ∈ 'success' | 'error' | 'danger' | 'warning' | 'info'   (default 'info')
 *   opts: { duration = 4000 (ms; 0 = sticky), title }
 * dismiss(id) removes a toast early.
 */
export function useToast() {
    const [toasts, setToasts] = useState([]);
    const idRef = useRef(0);
    const timers = useRef({});

    const dismiss = useCallback((id) => {
        setToasts((list) => list.filter((t) => t.id !== id));
        if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
    }, []);

    const notify = useCallback((message, kind = 'info', opts = {}) => {
        const id = ++idRef.current;
        const duration = opts.duration ?? 4000;
        setToasts((list) => [...list, { id, message, kind, title: opts.title }]);
        if (duration > 0) {
            timers.current[id] = setTimeout(() => dismiss(id), duration);
        }
        return id;
    }, [dismiss]);

    const toastNode = <ToastStack toasts={toasts} onDismiss={dismiss} />;

    return { notify, dismiss, toastNode };
}

const ICONS = {
    success: '✓',
    error: '!',
    danger: '!',
    warning: '⚠',
    info: 'i',
};

export function ToastStack({ toasts = [], onDismiss }) {
    if (!toasts.length) return null;
    return (
        <div className="cmn-toast-stack" role="region" aria-label="Notifications">
            <style>{TOAST_CSS}</style>
            {toasts.map((t) => {
                const kind = t.kind === 'danger' ? 'error' : t.kind;
                return (
                    <div key={t.id} className={`cmn-toast cmn-toast--${kind}`} role="status">
                        <span className="cmn-toast__icon" aria-hidden="true">{ICONS[kind] || ICONS.info}</span>
                        <div className="cmn-toast__body">
                            {t.title && <div className="cmn-toast__title">{t.title}</div>}
                            <div className="cmn-toast__msg">{t.message}</div>
                        </div>
                        <button type="button" className="cmn-toast__close" aria-label="Dismiss" onClick={() => onDismiss(t.id)}>×</button>
                    </div>
                );
            })}
        </div>
    );
}

const TOAST_CSS = `
.cmn-toast-stack {
    position: fixed; top: var(--space-3xl); right: var(--space-3xl); z-index: 10060;
    display: flex; flex-direction: column; gap: var(--space-xl);
    max-width: min(24rem, calc(100vw - var(--space-4xl)));
    font-family: var(--font-family);
}
.cmn-toast {
    display: flex; align-items: flex-start; gap: var(--space-xl);
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-left: var(--space-sm) solid var(--color-info);
    border-radius: var(--radius-sm);
    box-shadow: var(--shadow-lg);
    padding: var(--space-xl);
    animation: cmn-toast-in 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}
.cmn-toast--success { border-left-color: var(--color-success); }
.cmn-toast--error   { border-left-color: var(--color-danger); }
.cmn-toast--warning { border-left-color: var(--color-warning); }
.cmn-toast--info    { border-left-color: var(--color-info); }

.cmn-toast__icon {
    flex: 0 0 auto; width: var(--space-3xl); height: var(--space-3xl); margin-top: var(--space-xs);
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: var(--radius-pill); font-size: var(--fs-label); font-weight: var(--fw-bold);
    color: var(--color-text-inverse); background: var(--color-info);
}
.cmn-toast--success .cmn-toast__icon { background: var(--color-success); }
.cmn-toast--error   .cmn-toast__icon { background: var(--color-danger); }
.cmn-toast--warning .cmn-toast__icon { background: var(--color-warning); }

.cmn-toast__body { flex: 1 1 auto; min-width: 0; }
.cmn-toast__title { font-size: var(--fs-body-sm); font-weight: var(--fw-semibold); color: var(--color-text-primary); margin-bottom: var(--space-xs); }
.cmn-toast__msg { font-size: var(--fs-body-sm); line-height: 1.45; color: var(--color-text-secondary); word-break: break-word; }
.cmn-toast__close {
    flex: 0 0 auto; background: transparent; border: none; cursor: pointer;
    color: var(--color-text-muted); font-size: var(--fs-sub2); line-height: 1;
    padding: 0 var(--space-xs); margin: calc(var(--space-xs) * -1) calc(var(--space-xs) * -1) 0 0;
    transition: color 0.15s ease;
}
.cmn-toast__close:hover { color: var(--color-text-primary); }

@keyframes cmn-toast-in { from { opacity: 0; transform: translateX(var(--space-3xl)); } to { opacity: 1; transform: translateX(0); } }
`;

export default ToastStack;
